<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref } from 'vue'

  import { useI18n } from 'vue-i18n'

  import NotificationWeb from '@common/notification-layer/components/NotificationWeb.vue'

  import CPButton from '@ui/components/CPButton.vue'
  import CPSkeleton from '@ui/components/CPSkeleton.vue'

  import { twMerge } from 'tailwind-merge'
  import ProfileCard from './ProfileCard.vue'
  import TopBarChatButton from './TopBarChatButton.vue'
  import { useAuth } from '../../auth-layer/composables/useAuth'
  import { AuthStatus } from '../../auth-layer/store/auth.store'
  import { createElementId } from '../../core-layer/utils/core.utils'
  import { useCurrentUser } from '../../current-user-layer/composables/useCurrentUser'
  import { useLocation } from '../../location-layer/composables/useLocation'

  import type { LocationData } from '../../location-layer/types/location'

  const props = defineProps({
    heightClass: {
      type: String,
      default: 'h-13',
    },
    showBottomBorder: {
      type: Boolean,
      default: false,
    },
    /**
     * Show chat button - opt-in for apps that have chat functionality
     * Apps must wire @chatClick handler when enabling this
     */
    showChatButton: {
      type: Boolean,
      default: false,
    },
    chatUnreadCount: {
      type: Number,
      default: 0,
    },

    compactLeftSection: {
      type: Boolean,
      default: false,
    },
    class: {
      type: String,
      default: '',
    },
    showNotifications: {
      type: Boolean,
      default: true,
    },
    showProfileCard: {
      type: Boolean,
      default: true,
    },
  })

  const emit = defineEmits<{
    (e: 'loginClick'): void
    (e: 'signupClick'): void
    (e: 'logout'): void
    (e: 'chatClick'): void
  }>()

  const { t } = useI18n()

  const { store: locationStore } = useLocation()
  const { store: currentUserStore } = useCurrentUser()
  const { store: authStore } = useAuth()
  // Get location data
  const locationData = computed(() => {
    return locationStore.locationData as LocationData
  })

  // Auth and user state

  const isProfileLoading = computed(() => {
    return currentUserStore?.isLoadingProfile || false
  })
  const userProfile = computed(() => {
    return currentUserStore?.profile
  })

  const isAuthenticated = computed(() => {
    return authStore?.isAuthenticated || false
  })
  const isAuthLoading = computed(() => {
    // During SSR or before auth store is ready, show loading state
    if (!authStore) return true
    return authStore.status === AuthStatus.LOADING
  })
  const isProfileCardOpen = ref(false)

  // Language switcher
  // Only show languages that have actual translation files in the translation layer
  const supportedLanguages = ref([
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fr-FR', name: 'Français (France)', flag: '🇫🇷' },
  ])

  const currentLocale = ref('en')

  const isMobile = ref(false)
  const MOBILE_WIDTH = 768
  const handleResize = () => {
    if (!process.client) return
    isMobile.value = window.innerWidth <= MOBILE_WIDTH
  }

  // Notification state
  const notificationListIsOpen = ref(false)
  const appLauncherIsOpen = ref(false)

  const toggleNotificationList = () => {
    isProfileCardOpen.value = false
    appLauncherIsOpen.value = false
    notificationListIsOpen.value = !notificationListIsOpen.value
  }

  onMounted(() => {
    handleResize()
    if (process.client) window.addEventListener('resize', handleResize)
  })

  onUnmounted(() => {
    if (process.client) window.removeEventListener('resize', handleResize)
  })
</script>
<template>
  <header
    :class="
      twMerge([
        'sticky bg-sidebar top-0 z-40 box-border px-4',
        props.heightClass,
        props.class,
        props.showBottomBorder ? 'border-b-1 border border-l-0 border-r-0 border-t-0 border-solid border-sidebar' : '',
      ])
    "
  >
    <div class="grid h-full grid-cols-3 items-center gap-4">
      <!-- Left Section: Logo + Page Title + Left Slots -->
      <div class="left-section flex" :class="{ 'items-center gap-4': !props.compactLeftSection }">
        <!-- Logo Slot -->
        <div class="flex items-center">
          <slot name="logo" />
        </div>

        <!-- Page Title Slot (min-w-0 so CPBreadcrumb / long titles truncate inside the top bar grid) -->
        <div class="flex min-w-0 flex-1 flex-row items-center overflow-hidden">
          <slot name="page-title">
            <!-- Default: No page title -->
          </slot>
        </div>

        <!-- Left Slots -->
        <div class="flex items-center gap-2">
          <slot name="left" />
        </div>
      </div>

      <!-- Center Section: Search Bar + Center Slots -->
      <div class="center-section flex w-full max-w-2xl items-center justify-center">
        <slot name="center">
          <!-- Default: Search Bar -->
          <slot name="search">
            <!-- Search bar will be provided by parent components that need it -->
          </slot>
          <slot name="center-content" />
        </slot>
      </div>

      <!-- Right Section: Right Slots + Fixed Elements -->
      <div class="right-section flex items-center justify-end gap-4">
        <!-- Right Slots -->
        <div class="flex items-center gap-2">
          <slot name="right" />
        </div>

        <!-- Fixed Action Buttons -->
        <div class="flex items-center gap-3">
          <slot name="right-filter" />
          <!-- Chat Icon - Only shown when showChatButton is true -->
          <ClientOnly>
            <TopBarChatButton
              v-if="props.showChatButton && isAuthenticated && userProfile?.fullName"
              :button-id="createElementId('topbar', 'chat', 'button')"
              :badge-id="createElementId('topbar', 'chat', 'unread-badge')"
              :unread-count="props.chatUnreadCount"
              button-class="chat-icon-button !h-9 !w-9 !min-w-0 !rounded-lg !p-0 transition-colors duration-200 hover:!bg-hover"
              @click="emit('chatClick')"
            />
          </ClientOnly>
          <!-- Notifications -->
          <ClientOnly>
            <NotificationWeb
              v-if="props.showNotifications && isAuthenticated && userProfile?.fullName"
              :profile-card-is-open="isProfileCardOpen"
              :app-launcher-is-open="appLauncherIsOpen"
              :notification-list-is-open="notificationListIsOpen"
              :position="'end'"
              @update:notificationListIsOpen="toggleNotificationList"
            />
          </ClientOnly>
          <!-- Auth Loading State -->
          <template v-if="isAuthLoading">
            <div class="flex items-center gap-2">
              <CPSkeleton type="button" size="sm" width="64" height="32" />
              <CPSkeleton type="button" size="sm" width="64" height="32" />
            </div>
          </template>

          <!-- Authenticated State -->
          <template v-else-if="isAuthenticated">
            <div v-if="showProfileCard" class="flex items-center gap-2">
              <!-- Profile Loading State -->
              <template v-if="isProfileLoading">
                <CPSkeleton type="avatar" size="sm" />
              </template>
              <!-- Profile Loaded State -->
              <template v-else>
                <ProfileCard
                  :profile-card-is-open="isProfileCardOpen"
                  @update:profileCardIsOpen="isProfileCardOpen = $event"
                  @logout="emit('logout')"
                >
                  <template #profile-menu-extras-banner="{ close }">
                    <slot name="profile-menu-extras-banner" :close="close" />
                  </template>
                  <template #profile-menu-extras-items="{ close }">
                    <slot name="profile-menu-extras-items" :close="close" />
                  </template>
                </ProfileCard>
              </template>
            </div>
          </template>

          <!-- Not Authenticated State -->
          <template v-else>
            <slot name="action-buttons">
              <CPButton id="login" size="xs" type="secondary" @click="emit('loginClick')">
                {{ t('common.buttons.login') }}
              </CPButton>
              <CPButton id="signup" size="xs" type="primary" @click="emit('signupClick')">
                {{ t('common.buttons.signUp') }}
              </CPButton>
            </slot>
          </template>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
  /* Grid layout styles */
  .grid-cols-3 {
    grid-template-columns: 1fr auto 1fr;
  }

  /* Ensure proper spacing and alignment */
  .left-section {
    justify-self: start;
  }

  .center-section {
    justify-self: center;
  }

  .right-section {
    justify-self: end;
  }
</style>
